import {useState} from 'react'
import {SectionDivider} from '@/components/shared/SectionDivider'
import {Edit, Settings} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {SecretReferenceInput} from '@/components/shared/SecretReferenceInput'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {MutedText} from '@/components/shared/MutedText'
import {FormGrid} from '@/components/shared/FormGrid'
import {Stack} from '@/components/shared/Stack'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Icon} from '@/components/shared/Icon'
import {CodeText} from '@/components/shared/CodeText'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {AuthType} from '@/types/outbound-connector'
import type {OutboundConnector, RestHeaderEntry, RestOutboundConfigCreateRequest, RestOutboundConfigUpdateRequest} from '@/types/outbound-connector'
import {RestOperationHeadersEditor} from './RestOperationHeadersEditor'

 
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const
const REST_AUTH_OPTIONS = [
    {value: AuthType.BEARER_TOKEN as string, labelKey: 'bearerToken' as const},
    {value: AuthType.BASIC_AUTH as string, labelKey: 'basicAuth' as const},
    {value: AuthType.OAUTH2_CLIENT as string, labelKey: 'oauth2Client' as const},
    {value: AuthType.NONE as string, labelKey: 'noneAuth' as const},
]
 

interface RestFormState {
    baseUrl: string
    authType: AuthType
    authSecretRef: string
    tokenUrl: string
    clientId: string
    clientSecretRef: string
    scope: string
    grantMethod: string
    grantUrlTemplate: string
    grantBodyTemplate: string
    grantHeaders: RestHeaderEntry[]
    revokeMethod: string
    revokeUrlTemplate: string
    revokeBodyTemplate: string
    revokeHeaders: RestHeaderEntry[]
    successStatusCodes: string
    contentType: string
    connectTimeoutMs: number
    readTimeoutMs: number
    version: number
}


const DEFAULT_REST_FORM: RestFormState = {
    baseUrl: '',
    authType: AuthType.BEARER_TOKEN,
    authSecretRef: '',
    tokenUrl: '',
    clientId: '',
    clientSecretRef: '',
    scope: '',
    grantMethod: 'POST',
    grantUrlTemplate: '',
    grantBodyTemplate: '',
    grantHeaders: [],
    revokeMethod: 'DELETE',
    revokeUrlTemplate: '',
    revokeBodyTemplate: '',
    revokeHeaders: [],
    successStatusCodes: '200,201,204',
    contentType: 'application/json',
    connectTimeoutMs: 5000,
    readTimeoutMs: 10000,
    version: 0,
}
 

interface RestConfigCardProps {
    connector: OutboundConnector
    canUpdate: boolean
}

export function RestConfigCard({connector, canUpdate}: RestConfigCardProps) {
    const services = useServices()
    const i18n = useI18n()
    const createDialog = useDialog()
    const editDialog = useDialog()
    const [form, setForm] = useState<RestFormState>(DEFAULT_REST_FORM)

    const updateForm = (updates: Partial<RestFormState>) => setForm((prev) => ({...prev, ...updates}))

    const createMutation = useEntityMutation({
        mutationFn: (data: RestOutboundConfigCreateRequest) =>
            services.outboundConnectors.createRestConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: createDialog.close,
    })

    const updateMutation = useEntityMutation({
        mutationFn: (data: RestOutboundConfigUpdateRequest) =>
            services.outboundConnectors.updateRestConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: editDialog.close,
    })

    const openCreate = () => {
        setForm(DEFAULT_REST_FORM)
        createDialog.open()
    }

    const openEdit = () => {
        const config = connector.restConfig
        if (!config) return
        /* eslint-disable i18next/no-literal-string -- fallback values are technical constants */
        setForm({
            baseUrl: config.baseUrl,
            authType: config.authType,
            authSecretRef: config.authSecretRef ?? '',
            tokenUrl: config.tokenUrl ?? '',
            clientId: config.clientId ?? '',
            clientSecretRef: config.clientSecretRef ?? '',
            scope: config.scope ?? '',
            grantMethod: config.grantMethod,
            grantUrlTemplate: config.grantUrlTemplate,
            grantBodyTemplate: config.grantBodyTemplate ?? '',
            grantHeaders: [...config.grantHeaders],
            revokeMethod: config.revokeMethod,
            revokeUrlTemplate: config.revokeUrlTemplate,
            revokeBodyTemplate: config.revokeBodyTemplate ?? '',
            revokeHeaders: [...config.revokeHeaders],
            successStatusCodes: config.successStatusCodes ?? '200,201,204',
            contentType: config.contentType ?? 'application/json',
            connectTimeoutMs: config.connectTimeoutMs,
            readTimeoutMs: config.readTimeoutMs,
            version: config.version,
        })
        /* eslint-enable i18next/no-literal-string */
        editDialog.open()
    }

    const handleCreate = () => {
        const request: RestOutboundConfigCreateRequest = {
            baseUrl: form.baseUrl,
            authType: form.authType || undefined,
            authSecretRef: form.authSecretRef || undefined,
            tokenUrl: form.tokenUrl || undefined,
            clientId: form.clientId || undefined,
            clientSecretRef: form.clientSecretRef || undefined,
            scope: form.scope || undefined,
            grantMethod: form.grantMethod || undefined,
            grantUrlTemplate: form.grantUrlTemplate,
            grantBodyTemplate: form.grantBodyTemplate || undefined,
            grantHeaders: form.grantHeaders.length > 0 ? form.grantHeaders : undefined,
            revokeMethod: form.revokeMethod || undefined,
            revokeUrlTemplate: form.revokeUrlTemplate,
            revokeBodyTemplate: form.revokeBodyTemplate || undefined,
            revokeHeaders: form.revokeHeaders.length > 0 ? form.revokeHeaders : undefined,
            successStatusCodes: form.successStatusCodes || undefined,
            contentType: form.contentType || undefined,
            connectTimeoutMs: form.connectTimeoutMs || undefined,
            readTimeoutMs: form.readTimeoutMs || undefined,
        }
        createMutation.mutate(request)
    }

    const handleUpdate = () => {
        const request: RestOutboundConfigUpdateRequest = {
            baseUrl: form.baseUrl,
            authType: form.authType,
            authSecretRef: form.authSecretRef || null,
            tokenUrl: form.tokenUrl || null,
            clientId: form.clientId || null,
            clientSecretRef: form.clientSecretRef || null,
            scope: form.scope || null,
            grantMethod: form.grantMethod,
            grantUrlTemplate: form.grantUrlTemplate,
            grantBodyTemplate: form.grantBodyTemplate || null,
            grantHeaders: form.grantHeaders,
            revokeMethod: form.revokeMethod,
            revokeUrlTemplate: form.revokeUrlTemplate,
            revokeBodyTemplate: form.revokeBodyTemplate || null,
            revokeHeaders: form.revokeHeaders,
            successStatusCodes: form.successStatusCodes || null,
            contentType: form.contentType || null,
            connectTimeoutMs: form.connectTimeoutMs,
            readTimeoutMs: form.readTimeoutMs,
            version: form.version,
        }
        updateMutation.mutate(request)
    }

    const getAuthLabel = (type: AuthType) => {
        const opt = REST_AUTH_OPTIONS.find((o) => o.value === type)
        return opt ? i18n.connectors[opt.labelKey] : type
    }

    const restConfig = connector.restConfig

    const formFields = (mode: 'create' | 'edit') => {
        const handleSubmit = () => {
            if (mode === 'create') {
                handleCreate()
            } else {
                handleUpdate()
            }
        }
        return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            <div className="grid gap-6 py-4">
                <Stack>
                    <Strong>{i18n.connectors.restConfiguration}</Strong>
                    <FormGrid>
                        <FormGrid>
                            <Label htmlFor="rest-baseUrl">{i18n.connectors.baseUrl}</Label>
                            <Input id="rest-baseUrl" value={form.baseUrl}
                                onChange={(e) => updateForm({baseUrl: e.target.value})}
                                placeholder={i18n.connectors.restBaseUrlPlaceholder}/>
                        </FormGrid>
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label htmlFor="rest-authType">{i18n.connectors.authType}</Label>
                                <Select value={form.authType}
                                    onValueChange={(v) => updateForm({authType: v as AuthType})}>
                                    <SelectTrigger id="rest-authType" data-testid="select-rest-auth-type"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {REST_AUTH_OPTIONS.map(({value, labelKey}) => (
                                            <SelectItem key={value} value={value}>{i18n.connectors[labelKey]}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormGrid>
                            {(form.authType === AuthType.BEARER_TOKEN || form.authType === AuthType.BASIC_AUTH) && (
                                <FormGrid>
                                    <Label htmlFor="rest-authSecretRef">{i18n.connectors.authSecretRef}</Label>
                                    <SecretReferenceInput
                                        id="rest-authSecretRef"
                                        value={form.authSecretRef}
                                        onChange={(v) => updateForm({authSecretRef: v})}
                                        placeholder={i18n.connectors.authSecretRefPlaceholder}
                                        data-testid="rest-auth-secret-ref"
                                    />
                                </FormGrid>
                            )}
                        </DetailGrid>
                        {form.authType === AuthType.OAUTH2_CLIENT && (
                            /* eslint-disable i18next/no-literal-string -- OAuth2 field labels/placeholders pending translation */
                            <DetailGrid columns={2} responsive={false}>
                                <FormGrid>
                                    <Label htmlFor="rest-tokenUrl">Token URL</Label>
                                    <Input id="rest-tokenUrl" value={form.tokenUrl}
                                        onChange={(e) => updateForm({tokenUrl: e.target.value})}/>
                                </FormGrid>
                                <FormGrid>
                                    <Label htmlFor="rest-clientId">Client ID</Label>
                                    <Input id="rest-clientId" value={form.clientId}
                                        onChange={(e) => updateForm({clientId: e.target.value})}/>
                                </FormGrid>
                                <FormGrid>
                                    <Label htmlFor="rest-clientSecretRef">Client Secret Ref</Label>
                                    <Input id="rest-clientSecretRef" value={form.clientSecretRef}
                                        onChange={(e) => updateForm({clientSecretRef: e.target.value})}/>
                                </FormGrid>
                                <FormGrid>
                                    <Label htmlFor="rest-scope">Scope</Label>
                                    <Input id="rest-scope" value={form.scope}
                                        onChange={(e) => updateForm({scope: e.target.value})}/>
                                </FormGrid>
                            </DetailGrid>
                            /* eslint-enable i18next/no-literal-string */
                        )}
                    </FormGrid>
                </Stack>
                <Stack>
                    <Strong>{i18n.connectors.grantAction}</Strong>
                    <FormGrid>
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label htmlFor="rest-grantMethod">{i18n.connectors.httpMethod}</Label>
                                <Select value={form.grantMethod}
                                    onValueChange={(v) => updateForm({grantMethod: v})}>
                                    <SelectTrigger id="rest-grantMethod" data-testid="select-rest-grant-method"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {HTTP_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="rest-grantUrlTemplate">{i18n.connectors.urlTemplate}</Label>
                                <Input id="rest-grantUrlTemplate" value={form.grantUrlTemplate}
                                    onChange={(e) => updateForm({grantUrlTemplate: e.target.value})}
                                    placeholder={i18n.connectors.urlTemplatePlaceholder}/>
                            </FormGrid>
                        </DetailGrid>
                        <FormGrid>
                            <Label htmlFor="rest-grantBodyTemplate">{i18n.connectors.bodyTemplate}</Label>
                            <Input id="rest-grantBodyTemplate" value={form.grantBodyTemplate}
                                onChange={(e) => updateForm({grantBodyTemplate: e.target.value})}
                                placeholder={i18n.connectors.bodyTemplatePlaceholder}/>
                        </FormGrid>
                        <RestOperationHeadersEditor
                            operation="grant"
                            headers={form.grantHeaders}
                            onChange={(grantHeaders) => updateForm({grantHeaders})}
                        />
                    </FormGrid>
                </Stack>
                <Stack>
                    <Strong>{i18n.connectors.revokeAction}</Strong>
                    <FormGrid>
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label htmlFor="rest-revokeMethod">{i18n.connectors.httpMethod}</Label>
                                <Select value={form.revokeMethod}
                                    onValueChange={(v) => updateForm({revokeMethod: v})}>
                                    <SelectTrigger id="rest-revokeMethod" data-testid="select-rest-revoke-method"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {HTTP_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="rest-revokeUrlTemplate">{i18n.connectors.urlTemplate}</Label>
                                <Input id="rest-revokeUrlTemplate" value={form.revokeUrlTemplate}
                                    onChange={(e) => updateForm({revokeUrlTemplate: e.target.value})}
                                    placeholder={i18n.connectors.urlTemplatePlaceholder}/>
                            </FormGrid>
                        </DetailGrid>
                        <FormGrid>
                            <Label htmlFor="rest-revokeBodyTemplate">{i18n.connectors.bodyTemplate}</Label>
                            <Input id="rest-revokeBodyTemplate" value={form.revokeBodyTemplate}
                                onChange={(e) => updateForm({revokeBodyTemplate: e.target.value})}
                                placeholder={i18n.connectors.bodyTemplatePlaceholder}/>
                        </FormGrid>
                        <RestOperationHeadersEditor
                            operation="revoke"
                            headers={form.revokeHeaders}
                            onChange={(revokeHeaders) => updateForm({revokeHeaders})}
                        />
                    </FormGrid>
                </Stack>
                <Stack>
                    <Strong>{i18n.connectors.connectionSettings}</Strong>
                    <DetailGrid columns={2} responsive={false}>
                        <FormGrid>
                            <Label htmlFor="rest-successStatusCodes">{i18n.connectors.successStatusCodes}</Label>
                            <Input id="rest-successStatusCodes" value={form.successStatusCodes}
                                onChange={(e) => updateForm({successStatusCodes: e.target.value})}
                                placeholder={i18n.connectors.successStatusCodesPlaceholder}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="rest-contentType">{i18n.connectors.contentType}</Label>
                            <Input id="rest-contentType" value={form.contentType}
                                onChange={(e) => updateForm({contentType: e.target.value})}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="rest-connectTimeoutMs">{i18n.connectors.connectTimeoutMs}</Label>
                            <Input id="rest-connectTimeoutMs" type="number" value={form.connectTimeoutMs}
                                onChange={(e) => updateForm({connectTimeoutMs: parseInt(e.target.value)})}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="rest-readTimeoutMs">{i18n.connectors.readTimeoutMs}</Label>
                            <Input id="rest-readTimeoutMs" type="number" value={form.readTimeoutMs}
                                onChange={(e) => updateForm({readTimeoutMs: parseInt(e.target.value)})}/>
                        </FormGrid>
                    </DetailGrid>
                </Stack>
            </div>
            <SaveCancelFooter
                testIdPrefix={mode === 'create' ? 'create-rest' : 'edit-rest'}
                onCancel={() => mode === 'create' ? createDialog.close() : editDialog.close()}
                saving={mode === 'create' ? createMutation.isPending : updateMutation.isPending}
                disabled={!form.baseUrl || !form.grantUrlTemplate || !form.revokeUrlTemplate}
                saveLabel={mode === 'create' ? i18n.connectors.saveConfiguration : i18n.common.saveChanges}
            />
        </form>
        )
    }

    return (
        <>
            <Card data-testid="rest-config-card">
                <RowCardHeader>
                    <div>
                        <CardTitle className="items-center gap-2" data-testid="card-title-rest-config">
                            <Settings className="h-5 w-5"/>
                            {i18n.connectors.restConfiguration}
                        </CardTitle>
                        <CardDescription>{i18n.connectors.restDescription}</CardDescription>
                    </div>
                    {canUpdate && (
                        restConfig ? (
                            <Button data-testid="btn-edit-rest-config" size="sm" onClick={openEdit}>
                                <Icon icon={Edit} className="mr-2"/>
                                {i18n.common.edit}
                            </Button>
                        ) : (
                            <Button data-testid="btn-create-rest-config" size="sm" onClick={openCreate}>
                                <Icon icon={Settings} className="mr-2"/>
                                {i18n.connectors.configure}
                            </Button>
                        )
                    )}
                </RowCardHeader>
                <CardContent>
                    {restConfig ? (
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            <div className="sm:col-span-2">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.baseUrl}</dt>
                                <dd><CodeText>{restConfig.baseUrl}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.authType}</dt>
                                <dd><CodeText>{getAuthLabel(restConfig.authType)}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.contentType}</dt>
                                <dd><CodeText>{restConfig.contentType}</CodeText></dd>
                            </div>
                            {restConfig.authSecretRef && (
                                <div className="sm:col-span-2">
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.authSecretRef}</dt>
                                    <dd><CodeText>{restConfig.authSecretRef}</CodeText></dd>
                                </div>
                            )}
                            {/* eslint-disable i18next/no-literal-string -- OAuth2 field labels pending translation */}
                            {restConfig.tokenUrl && (
                                <div className="sm:col-span-2">
                                    <dt className="font-medium text-muted-foreground">Token URL</dt>
                                    <dd><CodeText>{restConfig.tokenUrl}</CodeText></dd>
                                </div>
                            )}
                            {restConfig.clientId && (
                                <div>
                                    <dt className="font-medium text-muted-foreground">Client ID</dt>
                                    <dd><CodeText>{restConfig.clientId}</CodeText></dd>
                                </div>
                            )}
                            {restConfig.clientSecretRef && (
                                <div>
                                    <dt className="font-medium text-muted-foreground">Client Secret Ref</dt>
                                    <dd><CodeText>{restConfig.clientSecretRef}</CodeText></dd>
                                </div>
                            )}
                            {restConfig.scope && (
                                <div className="sm:col-span-2">
                                    <dt className="font-medium text-muted-foreground">Scope</dt>
                                    <dd><CodeText>{restConfig.scope}</CodeText></dd>
                                </div>
                            )}
                            {/* eslint-enable i18next/no-literal-string */}
                            <SectionDivider colSpan={2}>
                                <Strong className="text-sm mb-2">{i18n.connectors.grantAction}</Strong>
                            </SectionDivider>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.httpMethod}</dt>
                                <dd><CodeText>{restConfig.grantMethod}</CodeText></dd>
                            </div>
                            <div className="sm:col-span-2">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.urlTemplate}</dt>
                                <dd><CodeText>{restConfig.grantUrlTemplate}</CodeText></dd>
                            </div>
                            {restConfig.grantBodyTemplate && (
                                <div className="sm:col-span-2">
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.bodyTemplate}</dt>
                                    <dd><CodeText>{restConfig.grantBodyTemplate}</CodeText></dd>
                                </div>
                            )}
                            {restConfig.grantHeaders.length > 0 && (
                                <div className="sm:col-span-2" data-testid="rest-grant-headers-display">
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.headers}</dt>
                                    <dd>
                                        {restConfig.grantHeaders.map((h) => (
                                            <div key={h.name}>
                                                <CodeText>{`${h.name}: ${h.value}`}</CodeText>
                                            </div>
                                        ))}
                                    </dd>
                                </div>
                            )}
                            <SectionDivider colSpan={2}>
                                <Strong className="text-sm mb-2">{i18n.connectors.revokeAction}</Strong>
                            </SectionDivider>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.httpMethod}</dt>
                                <dd><CodeText>{restConfig.revokeMethod}</CodeText></dd>
                            </div>
                            <div className="sm:col-span-2">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.urlTemplate}</dt>
                                <dd><CodeText>{restConfig.revokeUrlTemplate}</CodeText></dd>
                            </div>
                            {restConfig.revokeBodyTemplate && (
                                <div className="sm:col-span-2">
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.bodyTemplate}</dt>
                                    <dd><CodeText>{restConfig.revokeBodyTemplate}</CodeText></dd>
                                </div>
                            )}
                            {restConfig.revokeHeaders.length > 0 && (
                                <div className="sm:col-span-2" data-testid="rest-revoke-headers-display">
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.headers}</dt>
                                    <dd>
                                        {restConfig.revokeHeaders.map((h) => (
                                            <div key={h.name}>
                                                <CodeText>{`${h.name}: ${h.value}`}</CodeText>
                                            </div>
                                        ))}
                                    </dd>
                                </div>
                            )}
                            <SectionDivider colSpan={2}>
                                <Strong className="text-sm mb-2">{i18n.connectors.connectionSettings}</Strong>
                            </SectionDivider>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.successStatusCodes}</dt>
                                <dd><CodeText>{restConfig.successStatusCodes}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.connectTimeoutMs}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: restConfig.connectTimeoutMs})}</dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.readTimeoutMs}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: restConfig.readTimeoutMs})}</dd>
                            </div>
                        </dl>
                    ) : (
                        <MutedText block centered className="py-4">{i18n.connectors.restNotConfigured}</MutedText>
                    )}
                </CardContent>
            </Card>

            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.configureRestConnection}</DialogTitle>
                        <DialogDescription>{i18n.connectors.configureRestDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('create')}
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.editRestConfiguration}</DialogTitle>
                        <DialogDescription>{i18n.connectors.editRestDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('edit')}
                </DialogContent>
            </Dialog>
        </>
    )
}
